

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Box, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Switch } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/GetApp';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { GridOn } from '@mui/icons-material';


export default function Series() {
  const [series, setSeries] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ tipo_documento: '', serie: '', correlativo_actual: 1, id_sucursal: '' });
  const [editing, setEditing] = useState(null);
  const [tiposDocumento, setTiposDocumento] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(8);
  const [page, setPage] = useState(0);

  useEffect(() => {
    fetchSeries();
    fetchTiposDocumento();
    fetchSucursales();
  }, []);

  const fetchSucursales = async () => {
    const res = await axios.get('/api/sucursales');
    setSucursales(res.data);
  };

  const fetchSeries = async () => {
    const res = await axios.get('/api/series');
    setSeries(res.data);
  };

  const fetchTiposDocumento = async () => {
    const res = await axios.get('/api/tipo-documento');
    setTiposDocumento(res.data);
  };

  const handleOpen = (serie = null) => {
    setEditing(serie);
    if (serie) {
      setForm({
        ...serie,
        tipo_documento: serie.tipo_documento || '',
      });
    } else {
      setForm({ tipo_documento: '', serie: '', correlativo_actual: 1, id_sucursal: '' });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(null);
  };

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    try {
      if (editing) {
        await axios.put(`/api/series/${editing.id}`, form);
      } else {
        await axios.post('/api/series', form);
      }
      fetchSeries();
      handleClose();
    } catch (err) { alert(err.response?.data?.error || 'Error al guardar'); }
  }

  const handleDelete = async id => {
    try {
      await axios.delete(`/api/series/${id}`);
      fetchSeries();
    } catch { alert('Error al eliminar'); }
  };

  // Exportar Excel
  const handleExportExcel = async () => {
    const { exportGenericWorkbook } = await import('../utils/excel/genericWorkbook');
    const filtered = series.filter(row =>
      (row.serie || '').toLowerCase().includes(searchValue.toLowerCase()) ||
      (row.tipo_documento || '').toLowerCase().includes(searchValue.toLowerCase())
    );
    const rows = (rowsPerPage === -1 ? filtered : filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage))
      .map(row => ({
        id: row.id,
        sucursal: row.id_sucursal,
        tipo_documento: row.tipo_documento,
        serie: row.serie,
        correlativo_actual: row.correlativo_actual,
        estadoStr: row.activo ? 'ACTIVO' : 'INACTIVO'
      }));
    await exportGenericWorkbook({
      titulo: 'REPORTE - SERIES',
      sheetName: 'Series',
      tableName: 'tblSeries',
      fileName: 'Series',
      columns: [
        { key: 'id', label: 'ID', width: 8 },
        { key: 'sucursal', label: 'Sucursal', width: 20 },
        { key: 'tipo_documento', label: 'Tipo Documento', width: 22 },
        { key: 'serie', label: 'Serie', width: 14 },
        { key: 'correlativo_actual', label: 'Correlativo Actual', width: 20 },
        { key: 'estadoStr', label: 'Estado', width: 14, isEstado: true }
      ],
      rows,
      searchText: searchValue,
      usuario: ''
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1, mb: 4, width: '100%' }}>
      <Box sx={{ width: { xs: '100%', sm: '95%', md: '90%' }, maxWidth: 1800 }}>
        <Typography variant="h4" gutterBottom sx={{ textAlign: { xs: 'center', md: 'left' }, mb: 3, fontWeight: 'bold', color: '#222' }}>Series</Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
          <TextField size="small" label="Buscar" value={searchValue} onChange={e => { setSearchValue(e.target.value); setPage(0); }} sx={{ flex: 1 }} />
          <Button color="success" variant="contained" startIcon={<GridOn />} onClick={handleExportExcel} sx={{ minWidth: 120 }}>Exportar Excel</Button>
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => handleOpen()} sx={{ ml: { xs: 0, sm: 'auto' }, mt: { xs: 2, sm: 0 } }}>Nueva Serie</Button>
          <TextField
            size="small"
            label="Mostrar"
            type="number"
            value={rowsPerPage}
            onChange={e => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
            sx={{ width: 100 }}
          />
        </Box>
      <TableContainer component={Paper} sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', mt: 2, overflowX: 'auto' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="center"  sx={{ pr: 8 }}>ID</TableCell>
              <TableCell align="center">Sucursal</TableCell>
              <TableCell align="center">Tipo Documento</TableCell>
              <TableCell align="center">Serie</TableCell>
              <TableCell align="center">Correlativo Actual</TableCell>
              <TableCell align="center">Estado</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {series
              .filter(row =>
                (row.serie || '').toLowerCase().includes(searchValue.toLowerCase()) ||
                (row.tipo_documento || '').toLowerCase().includes(searchValue.toLowerCase())
              )
              .slice(rowsPerPage === -1 ? 0 : page * rowsPerPage, rowsPerPage === -1 ? undefined : (page + 1) * rowsPerPage)
              .map((row) => (
                <TableRow key={row.id}>
                  <TableCell align="center"  sx={{ pr: 8 }}>{row.id}</TableCell>
                  <TableCell align="center">{row.id_sucursal}</TableCell>
                  <TableCell align="center">{row.tipo_documento}</TableCell>
                  <TableCell align="center">{row.serie}</TableCell>
                  <TableCell align="center">{row.correlativo_actual}</TableCell>
                  <TableCell align="center">
                    <Switch checked={!!row.activo} color="success" disabled />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton color="primary" onClick={() => handleOpen(row)}><EditIcon /></IconButton>
                    <IconButton color="error" onClick={() => handleDelete(row.id)}><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Paginación fuera del recuadro, alineada a la derecha, texto plano */}
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 1, userSelect: 'none', fontSize: '0.9375rem', fontFamily: 'Roboto, Helvetica, Arial, sans-serif', fontWeight: 500 }}>
        <span
          style={{
            color: page === 0 ? '#aaa' : '#1976d2',
            cursor: page === 0 ? 'default' : 'pointer',
            marginRight: 16,
            fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
            fontWeight: 500
          }}
          onClick={() => page > 0 && setPage(page - 1)}
        >ANTERIOR</span>
        <span
          style={{
            color: (rowsPerPage === -1 ? true : (page + 1) * rowsPerPage >= series.filter(row =>
              (row.serie || '').toLowerCase().includes(searchValue.toLowerCase()) ||
              (row.tipo_documento || '').toLowerCase().includes(searchValue.toLowerCase())
            ).length) ? '#aaa' : '#1976d2',
            cursor: (rowsPerPage === -1 ? true : (page + 1) * rowsPerPage >= series.filter(row =>
              (row.serie || '').toLowerCase().includes(searchValue.toLowerCase()) ||
              (row.tipo_documento || '').toLowerCase().includes(searchValue.toLowerCase())
            ).length) ? 'default' : 'pointer',
            fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
            fontWeight: 500
          }}
          onClick={() => (rowsPerPage === -1 ? false : (page + 1) * rowsPerPage < series.filter(row =>
            (row.serie || '').toLowerCase().includes(searchValue.toLowerCase()) ||
            (row.tipo_documento || '').toLowerCase().includes(searchValue.toLowerCase())
          ).length) && setPage(page + 1)}
        >SIGUIENTE</span>
      </Box>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        PaperProps={{ sx: { width: 500, maxWidth: '90%' } }}
      >
        <DialogTitle>
          <Typography variant="h5" fontWeight="bold">
            {editing ? 'Editar Serie' : 'Nueva Serie'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
            <Autocomplete
              options={sucursales}
              getOptionLabel={option => option.nombre || ''}
              value={sucursales.find(s => s.id === form.id_sucursal) || null}
              onChange={(_, newValue) => setForm({ ...form, id_sucursal: newValue ? newValue.id : '' })}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              sx={{ width: '100%' }}
              renderInput={params => (
                <TextField {...params} label="Sucursal" margin="dense" fullWidth />
              )}
            />
            <Autocomplete
              options={tiposDocumento}
              getOptionLabel={option => option.descripcion ? `${option.descripcion} (${option.sunat})` : ''}
              value={tiposDocumento.find(td => td.sunat === form.tipo_documento) || null}
              onChange={(_, newValue) => setForm({ ...form, tipo_documento: newValue ? newValue.sunat : '' })}
              isOptionEqualToValue={(option, value) => option.sunat === value.sunat}
              sx={{ width: '100%' }}
              renderInput={params => (
                <TextField {...params} label="Tipo de Documento" margin="dense" fullWidth />
              )}
            />
            <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
              <TextField
                margin="dense"
                label="Serie"
                name="serie"
                value={form.serie}
                onChange={handleChange}
                sx={{ flex: 1 }}
              />
              <TextField
                margin="dense"
                label="Correlativo Actual"
                name="correlativo_actual"
                type="number"
                value={form.correlativo_actual}
                onChange={handleChange}
                sx={{ flex: 1 }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} startIcon={<CloseIcon />} color="error" variant="contained">Cancelar</Button>
          <Button onClick={handleSave} variant="contained" startIcon={<SaveIcon />} color="primary">Guardar</Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Box>
  );
}
